import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import AnalysisLoader from '../components/AnalysisLoader';
import FloatingNav from '../components/FloatingNav';
import { 
  Camera, 
  Upload, 
  X, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft, 
  Sparkles, 
  Loader2,
  Sun,
  Droplet,
  Target,
  Footprints,
  Image,
  Crown,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_661e4868-d868-458c-86d6-2d10e12396a2/artifacts/tx0slj8p_ChatGPT_Image_15_de_mai._de_2026__13_56_39-removebg-preview.png";

const NewReading = () => {
  const { user, subscription, readingsRemaining, api, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('guide'); // guide, camera, preview, uploading, pending, analyzing, result
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [pendingReading, setPendingReading] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAnalysisLoader, setShowAnalysisLoader] = useState(false);

  const canMakeReading = () => {
    if (subscription) {
      return readingsRemaining > 0;
    }
    return !user?.free_reading_used;
  };

  // Check if user can make reading on mount
  useEffect(() => {
    refreshSubscription();
  }, []);

  // Redirect to plans if can't make reading
  useEffect(() => {
    if (user && !canMakeReading()) {
      setStep('blocked');
    }
  }, [user, subscription, readingsRemaining]);

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Seu navegador não suporta acesso à câmera. Tente fazer upload de uma foto.');
        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: { ideal: 'environment' }, 
          width: { ideal: 1280 }, 
          height: { ideal: 1920 } 
        }
      });
      
      setStream(mediaStream);
      setStep('camera');
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(e => console.log('Video play error:', e));
        }
      }, 100);
      
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Permissão da câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador ou faça upload de uma foto.');
      } else if (err.name === 'NotFoundError') {
        setError('Nenhuma câmera encontrada. Tente fazer upload de uma foto.');
      } else if (err.name === 'NotReadableError') {
        setError('A câmera está em uso por outro aplicativo. Feche outros apps e tente novamente, ou faça upload de uma foto.');
      } else {
        setError('Não foi possível acessar a câmera. Tente fazer upload de uma foto.');
      }
    }
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageData);
      stopCamera();
      setStep('preview');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione uma imagem');
        return;
      }
      setUploadedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target?.result);
        setStep('preview');
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      
      if (uploadedFile) {
        formData.append('file', uploadedFile);
      } else if (capturedImage) {
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        formData.append('file', blob, 'foot.jpg');
      }

      const uploadResponse = await api.post('/readings/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPendingReading(uploadResponse.data);
      setStep('pending');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Erro ao enviar imagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeReading = async () => {
    if (!pendingReading) return;

    setShowAnalysisLoader(true);
    setError('');

    try {
      const response = await api.post(`/readings/${pendingReading.reading_id}/analyze`);
      await refreshSubscription();
      // Wait for loader animation to complete
      setTimeout(() => {
        setShowAnalysisLoader(false);
        navigate(`/leitura/${response.data.reading.id}`);
      }, 10000); // Loader takes ~10 seconds
    } catch (err) {
      setShowAnalysisLoader(false);
      console.error('Analysis error:', err);
      if (err.response?.status === 402) {
        setError(err.response.data.detail);
        setTimeout(() => navigate('/planos'), 2000);
      } else {
        setError(err.response?.data?.detail || 'Erro ao analisar imagem. Tente novamente.');
      }
    }
  };

  const resetCapture = () => {
    setCapturedImage(null);
    setUploadedFile(null);
    setPendingReading(null);
    setError('');
    setStep('guide');
  };

  const guidelines = [
    { icon: Sun, text: "Ambiente bem iluminado (luz natural é ideal)", color: "#F59E0B" },
    { icon: Droplet, text: "Pé limpo e seco", color: "#4DB6AC" },
    { icon: Target, text: "Câmera perpendicular ao solado", color: "#8B5CF6" },
    { icon: Footprints, text: "Fotografe o pé direito (solado)", color: "#2E5C55" },
  ];

  return (
    <div className="min-h-screen pb-28" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Analysis Loader */}
      <AnimatePresence>
        {showAnalysisLoader && (
          <AnalysisLoader 
            imageUrl={capturedImage || (uploadedFile && URL.createObjectURL(uploadedFile))}
          />
        )}
      </AnimatePresence>
      
      {/* Header */}
      <header
        className="px-4 py-5 flex items-center gap-4 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top right, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
        }}
      >
        <div className="absolute inset-0 scanner-grid opacity-25 pointer-events-none" />
        <button 
          onClick={() => {
            stopCamera();
            navigate(-1);
          }}
          className="relative z-10 p-2 text-white/80 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="relative z-10">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-cyan-300">Scanner IA</p>
          <h1 className="font-bold text-xl text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            Nova Leitura
          </h1>
        </div>
      </header>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3 animate-fade-in-up" data-testid="error-message">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Step: Blocked - No credits */}
      {step === 'blocked' && (
        <motion.div 
          className="px-4 py-8 max-w-lg mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#E8E4D9]/50 text-center">
            <motion.div 
              className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4A373]/20 to-[#C49363]/20 flex items-center justify-center mx-auto mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            >
              <Lock className="h-12 w-12 text-[#D4A373]" />
            </motion.div>
            
            <h2 className="font-bold text-2xl text-[#1C1C1E] mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
              Seu mapa completo está pronto
            </h2>
            
            <p className="text-[#8E8E93] mb-6 leading-relaxed">
              Desbloqueie a leitura detalhada com protocolo personalizado, zonas prioritárias e recomendações avançadas.
            </p>

            <div className="bg-gradient-to-br from-[#2E5C55]/5 to-[#3A7068]/5 rounded-2xl p-5 mb-6 border border-[#2E5C55]/10">
              <div className="flex items-center justify-center gap-3 text-[#48484A] mb-3">
                <Sparkles className="h-5 w-5 text-[#D4A373]" />
                <span className="font-medium">Experimente o Plus</span>
              </div>
              <div className="text-3xl font-bold text-[#2E5C55] mb-1">R$ 49,90<span className="text-base font-normal text-[#8E8E93]">/mês</span></div>
              <p className="text-xs text-[#8E8E93]">10 leituras + análise detalhada</p>
            </div>

            <Button 
              onClick={() => navigate('/planos')}
              className="w-full h-14 bg-gradient-to-r from-[#D4A373] to-[#C49363] hover:from-[#C49363] hover:to-[#B08353] text-white rounded-full text-lg font-semibold transition-all hover:shadow-xl hover:scale-[1.02]"
              data-testid="go-to-plans-btn"
            >
              <Crown className="h-5 w-5 mr-2" />
              Desbloquear meu mapa completo
            </Button>

            <button 
              onClick={() => navigate('/dashboard')}
              className="mt-4 text-[#8E8E93] hover:text-[#2E5C55] text-sm font-medium transition-colors"
            >
              Voltar ao início
            </button>
          </div>

          {/* Benefits list */}
          <div className="mt-6 space-y-3">
            {[
              { icon: Footprints, title: 'Leituras detalhadas', desc: 'Até 20 leituras por mês', color: '#2E5C55' },
              { icon: Sparkles, title: 'Mapas completos', desc: 'Análise de todas as zonas', color: '#4DB6AC' },
              { icon: Target, title: 'Protocolo personalizado', desc: 'Recomendações específicas', color: '#D4A373' }
            ].map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-[#E8E4D9]/50 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon className="h-5 w-5" style={{ color: item.color }} />
                </div>
                <div>
                  <p className="font-medium text-[#1C1C1E]">{item.title}</p>
                  <p className="text-sm text-[#8E8E93]">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Step: Guide */}
      {step === 'guide' && (
        <div className="px-4 py-6 max-w-lg mx-auto animate-fade-in-up">
          {/* Premium Scan Visual */}
          <div className="relative rounded-3xl overflow-hidden mb-6 p-8 text-center"
               style={{
                 background: 'radial-gradient(ellipse at center, #0F766E 0%, #083D3A 70%, #0A0F14 100%)',
                 boxShadow: '0 25px 60px rgba(8,61,58,0.4)',
                 border: '1px solid rgba(34,211,238,0.25)',
               }}>
            <div className="absolute inset-0 scanner-grid opacity-30 pointer-events-none" />
            <motion.div
              className="absolute left-0 right-0 h-[2px]"
              style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', boxShadow: '0 0 15px #22D3EE' }}
              animate={{ top: ['10%', '90%', '10%'] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="relative z-10 flex flex-col items-center">
              <div className="hud-chip mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                Scanner Pronto
              </div>
              <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center mb-4"
                   style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.4)', boxShadow: '0 0 30px rgba(34,211,238,0.4)' }}>
                <Camera className="h-10 w-10 text-cyan-300" />
              </div>
              <h2 className="font-bold text-2xl text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                Capture o solado do pé
              </h2>
              <p className="text-sm text-cyan-100/70 max-w-xs">
                Posicione o pé direito e siga o checklist abaixo para uma leitura premium.
              </p>
            </div>
          </div>

          {/* Guidelines Checklist */}
          <div className="bg-white rounded-3xl p-6 mb-6 shadow-lg" style={{ border: '1px solid rgba(15,118,110,0.1)' }}>
            <h3 className="font-bold text-[#0A0F14] mb-4 flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="w-1 h-5 rounded-full" style={{ background: 'linear-gradient(180deg, #22D3EE, #D4A256)' }} />
              Checklist de captura
            </h3>
            
            <div className="space-y-3">
              {guidelines.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="flex items-center gap-4 p-3 rounded-xl"
                  style={{ background: 'rgba(15,118,110,0.04)' }}
                >
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <item.icon className="h-5 w-5" style={{ color: item.color }} />
                  </div>
                  <span className="text-[#374151] flex-1 text-sm">{item.text}</span>
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={startCamera}
              className="w-full relative overflow-hidden rounded-2xl p-5 flex items-center gap-4 transition-all hover:shadow-2xl hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #083D3A 0%, #0F766E 50%, #14B8A6 100%)',
                boxShadow: '0 12px 40px rgba(15,118,110,0.45)',
              }}
              data-testid="open-camera-btn"
            >
              <motion.div
                className="absolute inset-y-0 w-1 opacity-50"
                style={{ background: 'linear-gradient(180deg, transparent, #22D3EE, transparent)', boxShadow: '0 0 20px #22D3EE' }}
                animate={{ left: ['-5%', '105%'] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
              />
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                   style={{ background: 'rgba(34,211,238,0.2)', border: '1px solid rgba(34,211,238,0.35)' }}>
                <Camera className="h-6 w-6 text-cyan-300" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-cyan-300">Recomendado</p>
                <p className="text-white font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>Abrir câmera</p>
              </div>
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button 
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="w-full h-14 bg-white rounded-2xl text-base font-bold flex items-center justify-center gap-3 transition-all hover:bg-cyan-50"
              style={{ border: '2px solid #0F766E', color: '#0F766E' }}
              data-testid="upload-btn"
            >
              <Image className="h-5 w-5" />
              Fazer upload de foto
            </Button>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 p-4 rounded-2xl flex items-start gap-3"
               style={{ background: 'rgba(212,162,86,0.08)', border: '1px solid rgba(212,162,86,0.25)' }}>
            <Sparkles className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D4A256' }} />
            <p className="text-sm" style={{ color: '#6B5826' }}>
              Conteúdo educacional. A leitura reflexológica não substitui diagnóstico médico.
            </p>
          </div>
        </div>
      )}

      {/* Step: Camera */}
      {step === 'camera' && (
        <div className="relative h-[calc(100vh-64px)] bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover bg-black"
            onLoadedMetadata={(e) => {
              e.target.play().catch(err => console.log('Play on metadata error:', err));
            }}
          />
          <canvas ref={canvasRef} className="hidden" />
          
          {/* Tech HUD overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {/* Scanner viewport */}
            <div className="relative w-[80%] max-w-[300px] aspect-[1/1.4]">
              {/* Corner brackets */}
              {['tl', 'tr', 'bl', 'br'].map((corner) => (
                <div
                  key={corner}
                  className={`scanner-corner ${corner}`}
                  style={{
                    position: 'absolute',
                    width: '32px',
                    height: '32px',
                    border: '3px solid #22D3EE',
                    boxShadow: '0 0 14px rgba(34,211,238,0.8)',
                    ...(corner === 'tl' && { top: 0, left: 0, borderRight: 'none', borderBottom: 'none', borderRadius: '12px 0 0 0' }),
                    ...(corner === 'tr' && { top: 0, right: 0, borderLeft: 'none', borderBottom: 'none', borderRadius: '0 12px 0 0' }),
                    ...(corner === 'bl' && { bottom: 0, left: 0, borderRight: 'none', borderTop: 'none', borderRadius: '0 0 0 12px' }),
                    ...(corner === 'br' && { bottom: 0, right: 0, borderLeft: 'none', borderTop: 'none', borderRadius: '0 0 12px 0' }),
                  }}
                />
              ))}
              
              {/* Scan line */}
              <motion.div
                className="absolute left-0 right-0 h-[2px] rounded-full"
                style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', boxShadow: '0 0 12px #22D3EE' }}
                animate={{ top: ['5%', '95%', '5%'] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              />
              
              {/* Center hint */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <Footprints className="h-10 w-10 text-cyan-300/80 mb-2 drop-shadow-lg" />
                <p className="text-white text-xs font-semibold tracking-wide bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full">
                  Posicione o solado aqui
                </p>
              </div>
            </div>

            {/* Top HUD */}
            <div className="absolute top-6 left-0 right-0 flex justify-center">
              <div className="hud-chip" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                AO VIVO
              </div>
            </div>
          </div>

          {/* Camera controls */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center gap-6 z-10">
            <button
              onClick={() => {
                stopCamera();
                setStep('guide');
              }}
              className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center hover:bg-black/80 transition-colors border border-white/20"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <button
              onClick={capturePhoto}
              className="relative w-20 h-20 rounded-full flex items-center justify-center hover:scale-105 transition-transform"
              style={{
                background: 'white',
                boxShadow: '0 0 0 6px rgba(34,211,238,0.3), 0 12px 40px rgba(0,0,0,0.5)',
              }}
              data-testid="capture-btn"
            >
              <div className="w-16 h-16 rounded-full"
                   style={{ background: 'linear-gradient(135deg, #0F766E, #22D3EE)' }} />
            </button>
            <div className="w-14" />
          </div>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && capturedImage && (
        <div className="px-4 py-6 max-w-lg mx-auto animate-fade-in-up">
          <div className="relative rounded-3xl overflow-hidden mb-6 shadow-xl">
            <img 
              src={capturedImage} 
              alt="Foto capturada" 
              className="w-full aspect-[3/4] object-cover"
              data-testid="preview-image"
            />
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-sm font-medium">
              Prévia
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={uploadImage}
              disabled={loading}
              className="w-full h-16 bg-gradient-to-r from-[#2E5C55] to-[#3A7068] hover:from-[#234744] hover:to-[#2E5C55] text-white rounded-2xl text-lg font-semibold transition-all hover:shadow-xl flex items-center justify-center gap-3"
              data-testid="confirm-upload-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-6 w-6" />
                  Confirmar e enviar
                </>
              )}
            </Button>
            
            <Button 
              onClick={resetCapture}
              variant="outline"
              className="w-full h-14 bg-white border-2 border-[#E8E4D9] text-[#48484A] rounded-2xl font-semibold hover:bg-[#FDFBF7]"
              disabled={loading}
            >
              Tirar outra foto
            </Button>
          </div>
        </div>
      )}

      {/* Step: Pending (blurred preview) */}
      {step === 'pending' && (
        <div className="px-4 py-6 max-w-lg mx-auto animate-fade-in-up">
          {/* Success Card */}
          <div className="bg-white rounded-3xl p-6 mb-6 shadow-lg border border-[#E8E4D9]/50 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2E5C55]/10 to-[#4DB6AC]/10 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-10 w-10 text-[#2E5C55]" />
            </div>
            <h2 className="font-bold text-xl text-[#1C1C1E] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Imagem pronta para análise!
            </h2>
            <p className="text-[#8E8E93]">
              {canMakeReading() 
                ? 'Use seus créditos para revelar a leitura completa.'
                : 'Você precisa de créditos para revelar a análise.'}
            </p>
          </div>

          {/* Blurred preview */}
          {capturedImage && (
            <div className="relative rounded-3xl overflow-hidden mb-6 shadow-lg">
              <img 
                src={capturedImage} 
                alt="Prévia" 
                className="w-full aspect-[3/4] object-cover blur-lg"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="text-center text-white">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="h-8 w-8" />
                  </div>
                  <p className="font-semibold text-lg">Análise bloqueada</p>
                  <p className="text-sm text-white/70">Clique abaixo para revelar</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {canMakeReading() ? (
              <Button 
                onClick={analyzeReading}
                disabled={loading}
                className="w-full h-16 bg-gradient-to-r from-[#D4A373] to-[#C49363] hover:from-[#C49363] hover:to-[#B08353] text-white rounded-2xl text-lg font-semibold transition-all hover:shadow-xl flex items-center justify-center gap-3"
                data-testid="use-credit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin" />
                    Analisando... (pode levar até 60s)
                  </>
                ) : (
                  <>
                    <Sparkles className="h-6 w-6" />
                    {subscription ? 'Usar 1 crédito e revelar' : 'Usar leitura gratuita'}
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/planos')}
                className="w-full h-16 bg-gradient-to-r from-[#2E5C55] to-[#3A7068] text-white rounded-2xl text-lg font-semibold"
                data-testid="go-to-plans-btn"
              >
                Ver planos disponíveis
              </Button>
            )}
            
            <Button 
              onClick={resetCapture}
              variant="outline"
              className="w-full h-14 bg-white border-2 border-[#E8E4D9] text-[#48484A] rounded-2xl font-semibold hover:bg-[#FDFBF7]"
              disabled={loading}
            >
              Cancelar
            </Button>
          </div>

          <div className="mt-6 p-4 bg-amber-50 rounded-2xl border-l-4 border-amber-400">
            <p className="text-sm text-amber-800">
              Conteúdo educacional e de bem-estar. Não constitui diagnóstico médico.
            </p>
          </div>
        </div>
      )}

      {/* Floating Nav (hidden on camera step to avoid blocking shutter) */}
      {step !== 'camera' && <FloatingNav disabledScan={!canMakeReading()} />}
    </div>
  );
};

export default NewReading;
